/**
 * components/payments/providerLogUi.js
 * ----------------------------------------------------------------------------
 * How the dashboard shows a payment provider log row (models/PaymentProviderLog.js
 * on the server): what MTN or Flutterwave said about one call, in words ops
 * uses. Shared by the Payment Provider Log page and the order payment review
 * dialog, so a row reads the same in both places.
 *
 * Nothing here can show a secret: the server never stores one. Phone numbers
 * arrive masked and cards as their last four digits.
 */
import React from "react";
import { Table } from "react-bootstrap";
import { PAGE, Pill, fmtMoney } from "../fiscal/fiscalUi.js";

export const PROVIDER_LABEL = Object.freeze({ MTN: "MTN MoMo", FLUTTERWAVE: "Flutterwave" });

export const OPERATION_LABEL = Object.freeze({
  TOKEN: "Sign in to MTN",
  ACCOUNT_CHECK: "Number check",
  REQUEST_TO_PAY: "Charge request",
  STATUS: "Status check",
  MOBILE_MONEY_CHARGE: "Mobile money charge",
  CARD_CHARGE: "Card charge",
  CARD_PIN_CHARGE: "Card PIN step",
  CARD_OTP_VALIDATE: "Card OTP step",
});

/**
 * REJECTED means the provider refused and holds nothing. UNKNOWN means no
 * definite answer, so the customer may still have been charged. ERROR means a
 * check or lookup of ours failed before or around the charge.
 */
export const OUTCOME = Object.freeze({
  ACCEPTED: { label: "Accepted", tone: "good", hint: "The provider took the charge request." },
  REJECTED: { label: "Refused", tone: "bad", hint: "The provider refused. Nothing was charged." },
  UNKNOWN: { label: "No clear answer", tone: "warn", hint: "No definite answer. Check the payment before charging again." },
  ERROR: { label: "Check failed", tone: "purple", hint: "A token, number or status check failed." },
});

export const OutcomePill = ({ outcome }) => {
  const meta = OUTCOME[outcome] || { label: String(outcome || "Unknown"), tone: "neutral", hint: "" };
  return <Pill tone={meta.tone} title={meta.hint}>{meta.label}</Pill>;
};

export const providerLabel = (provider) => PROVIDER_LABEL[provider] || provider || "Provider";
export const operationLabel = (operation) =>
  OPERATION_LABEL[operation] || String(operation || "").replace(/_/g, " ").toLowerCase();

/** "4 min ago", "3 h ago", "12 days ago". */
export const fmtAgo = (value, now = Date.now()) => {
  if (!value) return null;
  const at = new Date(value).getTime();
  if (Number.isNaN(at)) return null;
  const minutes = Math.max(0, Math.round((now - at) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

// Who asked for a status check (the row's `trigger`), in plain words. Tells
// "the app found the payment" from "the server's sweep did, minutes later".
const TRIGGER_LABEL = Object.freeze({
  client_poll: "found by the app",
  ticket_confirm: "found by the ticket screen",
  sweeper: "found by the server sweep",
  "sweeper-expiry": "found by the server sweep",
  mtn_callback: "found by MTN's callback",
  repair_half_settled: "repair script",
});
export const triggerLabel = (trigger) =>
  trigger ? TRIGGER_LABEL[trigger] || String(trigger).replace(/[_:-]+/g, " ") : null;

const channelLabel = (channel) => {
  if (!channel) return null;
  if (channel === "CARD_WEB") return "web";
  if (channel === "CARD_MOBILE") return "app";
  return channel.charAt(0) + channel.slice(1).toLowerCase();
};

// What an accepted call means, when the provider sent no words of its own.
// "None given" under a green Accepted read as if something were missing.
const ACCEPTED_TEXT = Object.freeze({
  TOKEN: "Signed in",
  ACCOUNT_CHECK: "Number is active on MoMo",
  REQUEST_TO_PAY: "Prompt sent to the customer's phone",
  STATUS: "Payment confirmed",
  MOBILE_MONEY_CHARGE: "Prompt sent to the customer's phone",
  CARD_CHARGE: "Card charge started",
  CARD_PIN_CHARGE: "PIN accepted",
  CARD_OTP_VALIDATE: "OTP accepted",
});

/**
 * A provider code with break points after its underscores only, so a long
 * code wraps between words ("INVALID_CALLBACK_URL_" / "HOST") instead of
 * mid-word ("INVALID_CALLBACK_URL_HOS" / "T").
 */
export const BreakableCode = ({ code }) => {
  const parts = String(code).split("_");
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 ? (
            <>
              _<wbr />
            </>
          ) : null}
        </React.Fragment>
      ))}
    </>
  );
};

/**
 * The provider's answer: its words first, its code under them. Flutterwave
 * sends no codes, so a "code" with spaces in it is shown as the sentence it
 * is. An accepted call with no words says what accepted means for that step.
 */
export const Reason = ({ code: rawCode, message: rawMessage, outcome, operation }) => {
  const isCode = Boolean(rawCode) && /^[A-Z0-9_.:-]+$/.test(String(rawCode));
  const code = isCode ? rawCode : null;
  const message = isCode ? rawMessage : rawMessage || rawCode;
  if (!code && !message) {
    if (outcome === "ACCEPTED") return <span style={{ color: "#334155" }}>{ACCEPTED_TEXT[operation] || "Accepted"}</span>;
    return <span style={PAGE.muted}>No reason given</span>;
  }
  return (
    <div style={{ minWidth: 200, maxWidth: 360 }}>
      {message && message !== code ? <div style={{ color: "#0f172a", lineHeight: 1.45 }}>{message}</div> : null}
      {code ? (
        <div style={{ ...PAGE.mono, whiteSpace: "normal", fontSize: 11, color: message ? "#64748b" : "#0f172a", fontWeight: message ? 500 : 700, marginTop: message ? 3 : 0 }}>
          <BreakableCode code={code} />
        </div>
      ) : null}
    </div>
  );
};

const payerOf = (row) => {
  if (row.msisdnMasked) return <span style={PAGE.mono}>{row.msisdnMasked}</span>;
  if (row.cardLast4) return <span>Card ending <span style={PAGE.mono}>{row.cardLast4}</span></span>;
  return <span style={PAGE.muted}>Not recorded</span>;
};

const shortId = (id) => (id ? `#${String(id).slice(-8)}` : null);

/** "15 Oct, 23:40:10": seconds, because a refusal and its retry land in the same minute. */
export const fmtStamp = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  return `${date}, ${time}`;
};

/**
 * A provider reference (an MTN request id is a 36-character UUID) as its first
 * eight characters in a chip. Clicking copies the whole thing; hovering shows
 * it. The full UUID wrapped onto five lines and set every row's height.
 */
export const ReferenceChip = ({ value }) => {
  const [copied, setCopied] = React.useState(false);
  if (!value) return <span style={PAGE.muted}>None</span>;
  const text = String(value);
  const short = text.length > 12 ? `${text.slice(0, 8)}…` : text;
  const copy = () => {
    try {
      navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      /* the title still shows it */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={`${text}\nClick to copy`}
      aria-label={`Reference ${text}. Copy`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        border: 0,
        borderRadius: 6,
        background: copied ? "#e6faf7" : "#eef2f7",
        color: copied ? "#047857" : "#334155",
        padding: "3px 8px",
        cursor: "pointer",
        ...PAGE.mono,
        fontSize: 11,
      }}
    >
      {copied ? "Copied" : short}
      <i className={`fas ${copied ? "fa-check" : "fa-copy"}`} aria-hidden="true" style={{ fontSize: 10, opacity: 0.7 }} />
    </button>
  );
};

const callNotes = (row) =>
  [
    row.httpStatus ? `HTTP ${row.httpStatus}` : null,
    Number.isFinite(row.durationMs) ? `${row.durationMs} ms` : null,
    row.provider === "MTN" && row.operation === "REQUEST_TO_PAY" && row.withCallback != null
      ? row.withCallback
        ? "with callback"
        : "without callback"
      : null,
    row.trigger ? triggerLabel(row.trigger) : null,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * One row per call to MTN or Flutterwave.
 *
 * `compact` is an order's own view (its payment review, its ticket page): the
 * order, payer, amount and customer columns go, and the calls read OLDEST
 * FIRST, numbered, because there they are one story ("refused, then accepted
 * without the callback"). The full log keeps newest first.
 * `onPickOrder(orderId)` makes the order cell a filter link.
 *
 * Dense rows and one text size across the columns (the dashboard's table
 * style); secondary facts sit on a smaller second line, the same size in
 * every column.
 */
export default function ProviderEventsTable({ rows, compact = false, onPickOrder, emptyText = "No provider calls match." }) {
  const list = Array.isArray(rows) ? rows : [];
  const ordered = compact
    ? [...list].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    : list;
  const columns = compact ? 6 : 9;
  const meta = { ...PAGE.meta, marginTop: 3 };
  return (
    <div className="lk-table-frame">
      <Table responsive hover className="lk-table lk-table--dense" style={{ fontSize: 13, marginBottom: 0 }}>
        <thead>
          <tr>
            {compact ? <th style={{ width: 36 }}>#</th> : null}
            <th>When</th>
            <th>Step</th>
            <th>Outcome</th>
            <th>Provider&apos;s answer</th>
            {compact ? null : <th>Customer was told</th>}
            {compact ? null : <th>Order</th>}
            {compact ? null : <th>Payer</th>}
            {compact ? null : <th style={{ textAlign: "right" }}>Amount</th>}
            <th>Reference</th>
          </tr>
        </thead>
        <tbody>
          {ordered.length === 0 ? (
            <tr>
              <td colSpan={columns} style={PAGE.empty}>{emptyText}</td>
            </tr>
          ) : (
            ordered.map((row, index) => (
              <tr key={row.id || index} style={{ verticalAlign: "top" }}>
                {compact ? <td style={{ color: "#94a3b8", fontWeight: 700 }}>{index + 1}</td> : null}
                <td style={{ whiteSpace: "nowrap" }} title={fmtAgo(row.createdAt) || undefined}>
                  <span style={{ color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{fmtStamp(row.createdAt)}</span>
                  {compact ? null : <span style={meta}>{fmtAgo(row.createdAt)}</span>}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <span style={PAGE.strong}>{operationLabel(row.operation)}</span>
                  <span style={meta}>{[providerLabel(row.provider), channelLabel(row.channel)].filter(Boolean).join(" · ")}</span>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <OutcomePill outcome={row.outcome} />
                  {callNotes(row) ? <span style={meta}>{callNotes(row)}</span> : null}
                </td>
                <td>
                  <Reason code={row.providerCode} message={row.providerMessage} outcome={row.outcome} operation={row.operation} />
                </td>
                {compact ? null : (
                  <td style={{ maxWidth: 260, color: "#334155" }}>
                    {row.customerMessage || (
                      <span style={PAGE.muted}>{row.outcome === "ACCEPTED" ? "No error shown" : "Nothing shown"}</span>
                    )}
                  </td>
                )}
                {compact ? null : (
                  <td style={{ whiteSpace: "nowrap" }}>
                    {row.orderId ? (
                      onPickOrder ? (
                        <button
                          type="button"
                          title={`Show every call for order ${row.orderId}`}
                          onClick={() => onPickOrder(row.orderId)}
                          style={{ background: "none", border: 0, padding: 0, color: "#083860", fontWeight: 600, cursor: "pointer" }}
                        >
                          <span style={PAGE.mono}>{shortId(row.orderId)}</span>
                        </button>
                      ) : (
                        <span style={PAGE.mono} title={row.orderId}>{shortId(row.orderId)}</span>
                      )
                    ) : (
                      <span style={PAGE.muted}>None</span>
                    )}
                    {row.country ? <span style={meta}>{row.country}</span> : null}
                  </td>
                )}
                {compact ? null : <td>{payerOf(row)}</td>}
                {compact ? null : (
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {fmtMoney(row.amount, row.currency) || <span style={PAGE.muted}>Not recorded</span>}
                  </td>
                )}
                <td style={{ whiteSpace: "nowrap" }}>
                  <ReferenceChip value={row.reference} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
}
